import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  ResponseErrorPanel,
  Table,
} from '@backstage/core-components';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type {
  CapabilityPackDefinition,
  DeploymentOperation,
  DriftObservation,
  DriftReconciliation,
  EntityScorecardResult,
  LifecycleAction,
  MonthlyCostObservation,
  ProjectEntitlement,
  QuotaReservation,
  ScorecardDefinition,
  TemplateMigration,
} from '@cubeangle/arta-common';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import AddAlarmIcon from '@material-ui/icons/AddAlarm';
import BlockIcon from '@material-ui/icons/Block';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  buildEntitlementRequest,
  emptyEntitlementForm,
  EntitlementFormState,
} from './entitlementForm';
import { useGovernanceStatus } from './useGovernanceStatus';
import { useArtaEdition } from '../../artaEdition';

interface GovernanceAuditEvent {
  id: number;
  entitlementId?: string;
  action: string;
  actor: string;
  outcome: string;
  detail: Record<string, unknown>;
  createdAt: string;
}

interface GovernanceNotification {
  id: number;
  outcome: string;
  severity: 'critical' | 'error' | 'warning' | 'info';
  entitlementId?: string;
  projectRef?: string;
  resourceKey?: string;
  reservationId?: string;
  leaseExpiresAt?: string;
  pipelineUrl?: string;
  createdAt: string;
}

interface MigrationItem {
  entityRef: string;
  kind: string;
  ownerRef?: string;
  sourceLocation?: string;
  status: 'ready' | 'legacy' | 'invalid';
  reason: string;
}

interface MigrationSummary {
  total: number;
  ready: number;
  legacy: number;
  invalid: number;
}

interface PreparedMigration {
  entityRef: string;
  sourceLocation?: string;
  annotations: Record<string, string>;
}

interface TemplateOption {
  entityRef: string;
  title: string;
  description?: string;
  type?: string;
  tags: string[];
  cloud?: 'aws' | 'azure';
  operation:
    | 'provider.create'
    | 'environment.create'
    | 'application.create'
    | 'resource.create';
}

interface CapabilityPackFormState {
  id: string;
  version: string;
  title: string;
  description: string;
  templateRefs: string[];
  estimatedMonthlyCost: string;
  mandatoryTags: string;
  allowedAdditionalTagKeys: string;
  allowAdditionalTags: boolean;
  maxAdditionalTags: string;
}

const emptyCapabilityPackForm: CapabilityPackFormState = {
  id: '',
  version: '1.0.0',
  title: '',
  description: '',
  templateRefs: [],
  estimatedMonthlyCost: '',
  mandatoryTags: '',
  allowedAdditionalTagKeys: '',
  allowAdditionalTags: true,
  maxAdditionalTags: '20',
};

function commaList(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/[\n,]/)
        .map(item => item.trim())
        .filter(Boolean),
    ),
  ];
}

function tagMap(value: string): Record<string, string> {
  return Object.fromEntries(
    commaList(value).map(item => {
      const separator = item.indexOf('=');
      if (separator < 1 || separator === item.length - 1) {
        throw new Error(`Tag ${item} must use key=value format`);
      }
      return [
        item.slice(0, separator).trim(),
        item.slice(separator + 1).trim(),
      ];
    }),
  );
}

function migrationYaml(result: PreparedMigration): string {
  return [
    'metadata:',
    '  annotations:',
    ...Object.entries(result.annotations).map(
      ([key, value]) => `    ${key}: ${JSON.stringify(value)}`,
    ),
  ].join('\n');
}

function scopeSummary(entitlement: ProjectEntitlement): string {
  const parts = [];
  if (entitlement.aws) {
    parts.push(`AWS ${entitlement.aws.accountIds.join(', ')}`);
  }
  if (entitlement.azure) {
    parts.push(`Azure ${entitlement.azure.subscriptionIds.join(', ')}`);
  }
  return parts.join(' · ');
}

function packSummary(entitlement: ProjectEntitlement): string {
  return entitlement.capabilityPacks
    .map(pack => `${pack.id}@${pack.version}`)
    .join('; ');
}

export const GovernancePage = () => {
  const { edition, awsEnabled, azureEnabled } = useArtaEdition();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const { status, error: statusError } = useGovernanceStatus();
  const [entitlements, setEntitlements] = useState<ProjectEntitlement[]>([]);
  const [packs, setPacks] = useState<CapabilityPackDefinition[]>([]);
  const [templateOptions, setTemplateOptions] = useState<TemplateOption[]>([]);
  const [audit, setAudit] = useState<GovernanceAuditEvent[]>([]);
  const [reservations, setReservations] = useState<QuotaReservation[]>([]);
  const [costObservations, setCostObservations] = useState<
    MonthlyCostObservation[]
  >([]);
  const [lifecycleActions, setLifecycleActions] = useState<LifecycleAction[]>(
    [],
  );
  const [deploymentOperations, setDeploymentOperations] = useState<
    DeploymentOperation[]
  >([]);
  const [templateMigrations, setTemplateMigrations] = useState<
    TemplateMigration[]
  >([]);
  const [scorecardDefinitions, setScorecardDefinitions] = useState<
    ScorecardDefinition[]
  >([]);
  const [scorecards, setScorecards] = useState<EntityScorecardResult[]>([]);
  const [driftObservations, setDriftObservations] = useState<
    DriftObservation[]
  >([]);
  const [driftReconciliations, setDriftReconciliations] = useState<
    DriftReconciliation[]
  >([]);
  const [upgradeEntityRef, setUpgradeEntityRef] = useState('');
  const [upgradePackKey, setUpgradePackKey] = useState('');
  const [upgradeTemplateRef, setUpgradeTemplateRef] = useState('');
  const [upgradePreservePaths, setUpgradePreservePaths] = useState('');
  const [upgradeReason, setUpgradeReason] = useState('');
  const [notifications, setNotifications] = useState<GovernanceNotification[]>(
    [],
  );
  const [migrationItems, setMigrationItems] = useState<MigrationItem[]>([]);
  const [migrationSummary, setMigrationSummary] = useState<MigrationSummary>();
  const [migrationEntityRef, setMigrationEntityRef] = useState('');
  const [migrationEntitlementId, setMigrationEntitlementId] = useState('');
  const [migrationPackKey, setMigrationPackKey] = useState('');
  const [migrationTemplateKey, setMigrationTemplateKey] = useState('');
  const [preparedMigration, setPreparedMigration] =
    useState<PreparedMigration>();
  const [preparingMigration, setPreparingMigration] = useState(false);
  const [form, setForm] = useState<EntitlementFormState>(emptyEntitlementForm);
  const [packForm, setPackForm] = useState<CapabilityPackFormState>(
    emptyCapabilityPackForm,
  );
  const [publishingPack, setPublishingPack] = useState(false);
  const [error, setError] = useState<Error>();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revoking, setRevoking] = useState('');

  const callBackend = useCallback(
    async (path: string, init?: RequestInit) => {
      const [baseUrl, credentials] = await Promise.all([
        discoveryApi.getBaseUrl('arta-governance'),
        identityApi.getCredentials(),
      ]);
      const response = await fetch(`${baseUrl}${path}`, {
        ...init,
        headers: {
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...(credentials.token
            ? { Authorization: `Bearer ${credentials.token}` }
            : {}),
          ...init?.headers,
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          body.error ?? `Request failed with HTTP ${response.status}`,
        );
      }
      return body;
    },
    [discoveryApi, identityApi],
  );

  const load = useCallback(async () => {
    if (!status?.isAdmin) return;
    setLoading(true);
    setError(undefined);
    try {
      const [
        entitlementResponse,
        packResponse,
        auditResponse,
        migrationResponse,
        reservationResponse,
        costObservationResponse,
        lifecycleActionResponse,
        deploymentOperationResponse,
        templateMigrationResponse,
        scorecardDefinitionResponse,
        scorecardResponse,
        driftObservationResponse,
        driftReconciliationResponse,
        templateOptionResponse,
      ] = await Promise.all([
        callBackend('/entitlements'),
        callBackend('/capability-packs'),
        callBackend('/audit'),
        callBackend('/migration/readiness'),
        callBackend('/reservations'),
        callBackend('/cost-observations'),
        callBackend('/lifecycle-actions'),
        callBackend('/deployment-operations'),
        callBackend('/template-migrations'),
        callBackend('/scorecard-definitions'),
        callBackend('/scorecards'),
        callBackend('/drift-observations'),
        callBackend('/drift-reconciliations'),
        callBackend('/template-options'),
      ]);
      setEntitlements(entitlementResponse.items ?? []);
      setPacks(packResponse.items ?? []);
      setAudit(auditResponse.items ?? []);
      setMigrationItems(migrationResponse.items ?? []);
      setMigrationSummary(migrationResponse.summary);
      setReservations(reservationResponse.items ?? []);
      setCostObservations(costObservationResponse.items ?? []);
      setLifecycleActions(lifecycleActionResponse.items ?? []);
      setDeploymentOperations(deploymentOperationResponse.items ?? []);
      setTemplateMigrations(templateMigrationResponse.items ?? []);
      setScorecardDefinitions(scorecardDefinitionResponse.items ?? []);
      setScorecards(scorecardResponse.items ?? []);
      setDriftObservations(driftObservationResponse.items ?? []);
      setDriftReconciliations(driftReconciliationResponse.items ?? []);
      setTemplateOptions(templateOptionResponse.items ?? []);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setLoading(false);
    }
  }, [callBackend, status?.isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMemberData = useCallback(async () => {
    if (!status || status.isAdmin) return;
    setLoading(true);
    setError(undefined);
    try {
      const [
        entitlementResponse,
        packResponse,
        reservationResponse,
        costResponse,
      ] = await Promise.all([
        callBackend('/entitlements'),
        callBackend('/capability-packs'),
        callBackend('/reservations'),
        callBackend('/cost-observations'),
      ]);
      setEntitlements(entitlementResponse.items ?? []);
      setPacks(packResponse.items ?? []);
      setReservations(reservationResponse.items ?? []);
      setCostObservations(costResponse.items ?? []);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setLoading(false);
    }
  }, [callBackend, status]);

  useEffect(() => {
    loadMemberData();
  }, [loadMemberData]);

  const loadNotifications = useCallback(async () => {
    if (!status) return;
    try {
      const response = await callBackend('/notifications');
      setNotifications(response.items ?? []);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }, [callBackend, status]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const migrationEntitlement = useMemo(
    () =>
      entitlements.find(
        entitlement => entitlement.id === migrationEntitlementId,
      ),
    [entitlements, migrationEntitlementId],
  );
  const migrationPack = useMemo(() => {
    if (!migrationPackKey) return undefined;
    return packs.find(
      pack => `${pack.id}@${pack.version}` === migrationPackKey,
    );
  }, [migrationPackKey, packs]);
  const migrationGrants = migrationEntitlement?.capabilityPacks ?? [];
  const migrationTemplates = (migrationPack?.templates ?? []).filter(template =>
    migrationGrants.some(
      grant =>
        grant.id.toLowerCase() === migrationPack?.id.toLowerCase() &&
        grant.version === migrationPack.version &&
        grant.operations.includes(template.operation),
    ),
  );

  const update = <K extends keyof EntitlementFormState>(
    field: K,
    value: EntitlementFormState[K],
  ) => setForm(current => ({ ...current, [field]: value }));

  const updatePackForm = <K extends keyof CapabilityPackFormState>(
    field: K,
    value: CapabilityPackFormState[K],
  ) => setPackForm(current => ({ ...current, [field]: value }));

  const createCapabilityPack = async () => {
    setPublishingPack(true);
    setError(undefined);
    try {
      if (!packForm.templateRefs.length) {
        throw new Error('Select at least one catalog template');
      }
      const templates = packForm.templateRefs.map(entityRef => {
        const template = templateOptions.find(
          option => option.entityRef === entityRef,
        );
        if (!template) throw new Error(`Unknown template ${entityRef}`);
        return {
          entityRef,
          operation: template.operation,
        };
      });
      const estimatedMonthlyCost = Number(packForm.estimatedMonthlyCost);
      if (!Number.isFinite(estimatedMonthlyCost) || estimatedMonthlyCost < 0) {
        throw new Error('Enter a valid architecture monthly estimate');
      }
      const maxAdditionalTags = Number(packForm.maxAdditionalTags);
      if (!Number.isInteger(maxAdditionalTags) || maxAdditionalTags < 0) {
        throw new Error('Maximum additional tags must be a whole number');
      }
      await callBackend('/capability-packs', {
        method: 'POST',
        body: JSON.stringify({
          id: packForm.id.trim(),
          version: packForm.version.trim(),
          title: packForm.title.trim(),
          ...(packForm.description.trim()
            ? { description: packForm.description.trim() }
            : {}),
          status: 'inactive',
          reviewStatus: 'draft',
          compatibility: { supportedEditions: [edition] },
          estimatedMonthlyCost,
          taggingPolicy: {
            mandatoryTags: tagMap(packForm.mandatoryTags),
            allowAdditionalTags: packForm.allowAdditionalTags,
            allowedAdditionalTagKeys: commaList(
              packForm.allowedAdditionalTagKeys,
            ),
            maxAdditionalTags,
          },
          templates,
        }),
      });
      setPackForm(emptyCapabilityPackForm);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPublishingPack(false);
    }
  };

  const approve = async () => {
    setSubmitting(true);
    setError(undefined);
    try {
      const request = buildEntitlementRequest(form, packs, edition);
      await callBackend('/entitlements', {
        method: 'POST',
        body: JSON.stringify(request),
      });
      setForm(emptyEntitlementForm);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  };

  const revoke = async (entitlement: ProjectEntitlement) => {
    if (
      !window.confirm(
        `Revoke '${entitlement.id}'? This blocks new authorized operations but does not delete existing resources.`,
      )
    ) {
      return;
    }
    setRevoking(entitlement.id);
    setError(undefined);
    try {
      await callBackend(
        `/entitlements/${encodeURIComponent(entitlement.id)}/revoke`,
        {
          method: 'POST',
        },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setRevoking('');
    }
  };

  const releaseReservation = async (reservation: QuotaReservation) => {
    const evidence = window.prompt(
      `Record cleanup evidence for '${reservation.resourceKey}' (for example, teardown run URL or change ticket).`,
    );
    if (!evidence?.trim()) return;
    if (
      !window.confirm(
        'Confirm that cloud resources are deleted, pipeline state is reconciled, and the catalog entity is removed.',
      )
    )
      return;
    setError(undefined);
    try {
      await callBackend(
        `/reservations/${encodeURIComponent(reservation.id)}/cleanup-verified`,
        {
          method: 'POST',
          body: JSON.stringify({
            evidence: evidence.trim(),
            cloudResourcesDeleted: true,
            catalogEntityRemoved: true,
            pipelineStateReconciled: true,
          }),
        },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const extendLease = async (reservation: QuotaReservation) => {
    const daysText = window.prompt(
      `How many days should '${reservation.resourceKey}' be extended?`,
      '7',
    );
    if (!daysText) return;
    const additionalDays = Number(daysText);
    if (!Number.isInteger(additionalDays) || additionalDays <= 0) {
      setError(new Error('Lease extension days must be a positive integer'));
      return;
    }
    const reason = window.prompt(
      'Record the approval reason for this extension.',
    );
    if (!reason?.trim()) return;
    setError(undefined);
    try {
      await callBackend(
        `/reservations/${encodeURIComponent(reservation.id)}/extend-lease`,
        {
          method: 'POST',
          body: JSON.stringify({ additionalDays, reason: reason.trim() }),
        },
      );
      await Promise.all([load(), loadNotifications()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const approveLifecycleAction = async (item: LifecycleAction) => {
    setError(undefined);
    try {
      await callBackend(
        `/lifecycle-actions/${encodeURIComponent(item.id)}/approve`,
        { method: 'POST' },
      );
      await Promise.all([load(), loadNotifications()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const cancelLifecycleAction = async (item: LifecycleAction) => {
    const reason = window.prompt(
      `Why should the ${item.action} recommendation for '${
        item.entityRef ?? item.reservationId
      }' be cancelled?`,
    );
    if (!reason?.trim()) return;
    setError(undefined);
    try {
      await callBackend(
        `/lifecycle-actions/${encodeURIComponent(item.id)}/status`,
        {
          method: 'POST',
          body: JSON.stringify({
            status: 'cancelled',
            resultMessage: reason.trim(),
          }),
        },
      );
      await Promise.all([load(), loadNotifications()]);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const approveDeploymentOperation = async (item: DeploymentOperation) => {
    setError(undefined);
    try {
      await callBackend(
        `/deployment-operations/${encodeURIComponent(item.id)}/approve`,
        { method: 'POST' },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const cancelDeploymentOperation = async (item: DeploymentOperation) => {
    const reason = window.prompt(
      `Why should rollback request '${item.id}' be cancelled?`,
    );
    if (!reason?.trim()) return;
    setError(undefined);
    try {
      await callBackend(
        `/deployment-operations/${encodeURIComponent(item.id)}/status`,
        {
          method: 'POST',
          body: JSON.stringify({
            status: 'cancelled',
            resultMessage: reason.trim(),
          }),
        },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const approveDriftReconciliation = async (item: DriftReconciliation) => {
    try {
      await callBackend(
        `/drift-reconciliations/${encodeURIComponent(item.id)}/approve`,
        { method: 'POST' },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const cancelDriftReconciliation = async (item: DriftReconciliation) => {
    try {
      await callBackend(
        `/drift-reconciliations/${encodeURIComponent(item.id)}/status`,
        {
          method: 'POST',
          body: JSON.stringify({
            status: 'cancelled',
            resultMessage: 'Cancelled by platform administrator',
          }),
        },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const reviewPack = async (
    pack: CapabilityPackDefinition,
    reviewStatus: 'in-review' | 'approved' | 'rejected',
  ) => {
    const reviewNote = window.prompt(
      `Review note for ${pack.id}@${pack.version}:`,
    );
    if (!reviewNote?.trim()) return;
    try {
      await callBackend(
        `/capability-packs/${encodeURIComponent(pack.id)}/${encodeURIComponent(
          pack.version,
        )}/review`,
        {
          method: 'POST',
          body: JSON.stringify({ reviewStatus, reviewNote: reviewNote.trim() }),
        },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const changePackLifecycle = async (
    pack: CapabilityPackDefinition,
    lifecycleStatus: 'active' | 'deprecated' | 'retired',
  ) => {
    const reason = window.prompt(
      `Reason to mark ${pack.id}@${pack.version} ${lifecycleStatus}:`,
    );
    if (!reason?.trim()) return;
    const successorVersion =
      lifecycleStatus === 'deprecated'
        ? window.prompt('Optional active successor version:')?.trim() ||
          undefined
        : undefined;
    try {
      await callBackend(
        `/capability-packs/${encodeURIComponent(pack.id)}/${encodeURIComponent(
          pack.version,
        )}/lifecycle`,
        {
          method: 'POST',
          body: JSON.stringify({
            status: lifecycleStatus,
            successorVersion,
            reason: reason.trim(),
          }),
        },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const approveTemplateMigration = async (item: TemplateMigration) => {
    try {
      await callBackend(
        `/template-migrations/${encodeURIComponent(item.id)}/approve`,
        { method: 'POST' },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const cancelTemplateMigration = async (item: TemplateMigration) => {
    const resultMessage = window.prompt(
      `Why should migration '${item.id}' be cancelled?`,
    );
    if (!resultMessage?.trim()) return;
    try {
      await callBackend(
        `/template-migrations/${encodeURIComponent(item.id)}/status`,
        {
          method: 'POST',
          body: JSON.stringify({
            status: 'cancelled',
            resultMessage: resultMessage.trim(),
          }),
        },
      );
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const requestTemplateMigration = async () => {
    const separator = upgradePackKey.lastIndexOf('@');
    if (
      !upgradeEntityRef ||
      separator < 1 ||
      !upgradeTemplateRef ||
      upgradeReason.trim().length < 10
    ) {
      setError(
        new Error(
          'Entity, target pack, template, and a migration reason are required',
        ),
      );
      return;
    }
    try {
      await callBackend('/template-migrations', {
        method: 'POST',
        body: JSON.stringify({
          entityRef: upgradeEntityRef,
          toVersion: upgradePackKey.slice(separator + 1),
          toTemplateRef: upgradeTemplateRef,
          preservePaths: upgradePreservePaths
            .split(',')
            .map(value => value.trim())
            .filter(Boolean),
          reason: upgradeReason.trim(),
        }),
      });
      setUpgradeEntityRef('');
      setUpgradePackKey('');
      setUpgradeTemplateRef('');
      setUpgradePreservePaths('');
      setUpgradeReason('');
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  };

  const prepareMigration = async () => {
    if (
      !migrationEntityRef ||
      !migrationEntitlement ||
      !migrationPack ||
      !migrationTemplateKey
    ) {
      setError(
        new Error(
          'Select an entity, entitlement, capability pack, and template operation',
        ),
      );
      return;
    }
    const template = migrationTemplates.find(
      item => `${item.entityRef}|${item.operation}` === migrationTemplateKey,
    );
    if (!template) {
      setError(new Error('The selected template operation is unavailable'));
      return;
    }
    setPreparingMigration(true);
    setPreparedMigration(undefined);
    setError(undefined);
    try {
      const result = await callBackend('/migration/prepare', {
        method: 'POST',
        body: JSON.stringify({
          entityRef: migrationEntityRef,
          entitlementId: migrationEntitlement.id,
          capabilityPackId: migrationPack.id,
          capabilityPackVersion: migrationPack.version,
          templateRef: template.entityRef,
          operation: template.operation,
        }),
      });
      setPreparedMigration(result);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setPreparingMigration(false);
    }
  };

  if (!status && !statusError) return <Progress />;

  return (
    <Page themeId="tool">
      <Header
        title="Project Entitlements"
        subtitle="Approve reusable capability packs for an exact project, team, and cloud scope"
      />
      <Content>
        {statusError && <ResponseErrorPanel error={statusError} />}
        {error && <ResponseErrorPanel error={error} />}
        {status && (
          <Table
            title={`My Lifecycle Alerts (${notifications.length})`}
            options={{ paging: true, pageSize: 5, search: true }}
            columns={[
              {
                title: 'Time',
                render: row => new Date(row.createdAt).toLocaleString(),
              },
              { title: 'Severity', field: 'severity' },
              { title: 'Project', field: 'projectRef' },
              { title: 'Resource', field: 'resourceKey' },
              {
                title: 'Event',
                render: row => row.outcome.replace(/^reservation-/, ''),
              },
              {
                title: 'Lease expires',
                render: row =>
                  row.leaseExpiresAt
                    ? new Date(row.leaseExpiresAt).toLocaleString()
                    : '',
              },
              {
                title: 'Pipeline',
                render: row =>
                  row.pipelineUrl ? (
                    <a
                      href={row.pipelineUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open run
                    </a>
                  ) : (
                    ''
                  ),
              },
            ]}
            data={notifications}
            actions={[
              {
                icon: () => <RefreshIcon />,
                tooltip: 'Refresh lifecycle alerts',
                isFreeAction: true,
                onClick: loadNotifications,
              },
            ]}
          />
        )}
        {status && !status.isAdmin && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InfoCard title="My approved architectures">
                <Typography>
                  Your identity, <code>{status.actorRef}</code>, can deploy the
                  active architecture versions shown below from the Create page.
                  Estimates are reviewed forecasts; actual cloud costs can take
                  time to appear.
                </Typography>
              </InfoCard>
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Architecture Entitlements (${entitlements.length})`}
                options={{ paging: false, search: true }}
                columns={[
                  { title: 'Project', field: 'projectRef' },
                  { title: 'Architectures', render: row => packSummary(row) },
                  { title: 'Cloud scope', render: row => scopeSummary(row) },
                  { title: 'Status', render: row => row.status ?? 'active' },
                  {
                    title: 'Monthly ceiling',
                    render: row =>
                      row.quotas?.maxEstimatedMonthlyCost ?? 'Not configured',
                  },
                ]}
                data={entitlements}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Architecture Cost Tracking (${costObservations.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  {
                    title: 'Architecture',
                    render: row =>
                      `${row.capabilityPackId}@${row.capabilityPackVersion}`,
                  },
                  { title: 'Project', field: 'projectRef' },
                  {
                    title: 'Environment',
                    render: row =>
                      row.tags?.['arta-environment'] ?? 'Unclassified',
                  },
                  { title: 'Cloud', field: 'source' },
                  {
                    title: 'Actual cost',
                    render: row => `${row.currency} ${row.amount.toFixed(2)}`,
                  },
                  {
                    title: 'Period end',
                    render: row => new Date(row.periodEnd).toLocaleDateString(),
                  },
                ]}
                data={costObservations}
              />
            </Grid>
          </Grid>
        )}
        {status?.isAdmin && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InfoCard title="Enforcement rollout">
                <Typography>
                  Template access enforcement is currently{' '}
                  <strong>
                    {status.enforceTemplateAccess ? 'enabled' : 'disabled'}
                  </strong>
                  . Publish and validate capability packs and project grants
                  before enabling it for developers. Catalog migration is ready
                  when every ARTA entity below reports ready.
                </Typography>
              </InfoCard>
            </Grid>

            <Grid item xs={12}>
              <Table
                title={`Catalog Migration Readiness (${
                  migrationSummary?.ready ?? 0
                }/${migrationSummary?.total ?? 0} ready)`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Entity', field: 'entityRef' },
                  { title: 'Kind', field: 'kind' },
                  { title: 'Owner', field: 'ownerRef' },
                  { title: 'Status', field: 'status' },
                  { title: 'Reason', field: 'reason' },
                  { title: 'Source', field: 'sourceLocation' },
                ]}
                data={migrationItems}
                actions={[
                  {
                    icon: () => <RefreshIcon />,
                    tooltip: 'Refresh migration readiness',
                    isFreeAction: true,
                    onClick: load,
                  },
                ]}
              />
            </Grid>

            {(migrationSummary?.legacy || migrationSummary?.invalid) && (
              <Grid item xs={12}>
                <InfoCard title="Prepare Existing Entity Migration">
                  <Typography paragraph>
                    Select the approved entitlement and original template. ARTA
                    validates the owner and cloud target, then creates signed
                    annotations for the entity's source catalog file.
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required variant="outlined">
                        <InputLabel>Existing entity</InputLabel>
                        <Select
                          label="Existing entity"
                          value={migrationEntityRef}
                          onChange={event => {
                            setMigrationEntityRef(String(event.target.value));
                            setPreparedMigration(undefined);
                          }}
                        >
                          {migrationItems
                            .filter(item => item.status !== 'ready')
                            .map(item => (
                              <MenuItem
                                key={item.entityRef}
                                value={item.entityRef}
                              >
                                {item.entityRef} ({item.status})
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required variant="outlined">
                        <InputLabel>Active entitlement</InputLabel>
                        <Select
                          label="Active entitlement"
                          value={migrationEntitlementId}
                          onChange={event => {
                            setMigrationEntitlementId(
                              String(event.target.value),
                            );
                            setMigrationPackKey('');
                            setMigrationTemplateKey('');
                            setPreparedMigration(undefined);
                          }}
                        >
                          {entitlements
                            .filter(
                              entitlement =>
                                (entitlement.status ?? 'active') === 'active',
                            )
                            .map(entitlement => (
                              <MenuItem
                                key={entitlement.id}
                                value={entitlement.id}
                              >
                                {entitlement.id} — {entitlement.projectRef}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required variant="outlined">
                        <InputLabel>Granted capability pack</InputLabel>
                        <Select
                          label="Granted capability pack"
                          value={migrationPackKey}
                          onChange={event => {
                            setMigrationPackKey(String(event.target.value));
                            setMigrationTemplateKey('');
                            setPreparedMigration(undefined);
                          }}
                        >
                          {migrationGrants.map(grant => (
                            <MenuItem
                              key={`${grant.id}@${grant.version}`}
                              value={`${grant.id}@${grant.version}`}
                            >
                              {grant.id}@{grant.version}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth required variant="outlined">
                        <InputLabel>Original template operation</InputLabel>
                        <Select
                          label="Original template operation"
                          value={migrationTemplateKey}
                          onChange={event => {
                            setMigrationTemplateKey(String(event.target.value));
                            setPreparedMigration(undefined);
                          }}
                        >
                          {migrationTemplates.map(template => (
                            <MenuItem
                              key={`${template.entityRef}|${template.operation}`}
                              value={`${template.entityRef}|${template.operation}`}
                            >
                              {template.entityRef} — {template.operation}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        color="primary"
                        variant="contained"
                        disabled={preparingMigration}
                        onClick={prepareMigration}
                      >
                        {preparingMigration
                          ? 'Preparing…'
                          : 'Prepare signed annotations'}
                      </Button>
                    </Grid>
                    {preparedMigration && (
                      <Grid item xs={12}>
                        <Typography paragraph>
                          Apply this block to{' '}
                          <code>
                            {preparedMigration.sourceLocation ??
                              preparedMigration.entityRef}
                          </code>{' '}
                          and refresh the catalog entity.
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={10}
                          variant="outlined"
                          label="Signed catalog annotations"
                          value={migrationYaml(preparedMigration)}
                          InputProps={{ readOnly: true }}
                        />
                        <Button
                          style={{ marginTop: 12 }}
                          variant="outlined"
                          onClick={() =>
                            navigator.clipboard.writeText(
                              migrationYaml(preparedMigration),
                            )
                          }
                        >
                          Copy annotations
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </InfoCard>
              </Grid>
            )}

            <Grid item xs={12}>
              <InfoCard title="Approve Project Capability">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Entitlement ID"
                      helperText="Stable lowercase ID, for example payments-databricks"
                      value={form.id}
                      onChange={event => update('id', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Approved project"
                      helperText="Component ref, for example component:default/payments"
                      value={form.projectRef}
                      onChange={event =>
                        update('projectRef', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Approved users or groups"
                      helperText="Comma or newline separated Backstage entity refs"
                      value={form.subjectRefs}
                      onChange={event =>
                        update('subjectRefs', event.target.value)
                      }
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth required variant="outlined">
                      <InputLabel>Capability packs</InputLabel>
                      <Select
                        multiple
                        label="Capability packs"
                        value={form.packKeys}
                        renderValue={selected =>
                          (selected as string[]).join(', ')
                        }
                        onChange={event =>
                          update('packKeys', event.target.value as string[])
                        }
                      >
                        {packs
                          .filter(
                            pack => (pack.status ?? 'active') === 'active',
                          )
                          .map(pack => (
                            <MenuItem
                              key={`${pack.id}@${pack.version}`}
                              value={`${pack.id}@${pack.version}`}
                            >
                              {pack.title} ({pack.id}@{pack.version})
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {awsEnabled && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required={edition === 'aws'}
                          variant="outlined"
                          label="AWS account IDs"
                          helperText="Comma separated approved AWS accounts"
                          value={form.awsAccountIds}
                          onChange={event =>
                            update('awsAccountIds', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="AWS regions"
                          value={form.awsRegions}
                          onChange={event =>
                            update('awsRegions', event.target.value)
                          }
                        />
                      </Grid>
                    </>
                  )}
                  {azureEnabled && (
                    <>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          required={edition === 'azure'}
                          variant="outlined"
                          label="Azure subscription IDs"
                          helperText="Comma separated approved Azure subscriptions"
                          value={form.azureSubscriptionIds}
                          onChange={event =>
                            update('azureSubscriptionIds', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Azure regions"
                          value={form.azureRegions}
                          onChange={event =>
                            update('azureRegions', event.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Azure resource groups"
                          value={form.azureResourceGroups}
                          onChange={event =>
                            update('azureResourceGroups', event.target.value)
                          }
                        />
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Starts at"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      value={form.startsAt}
                      onChange={event => update('startsAt', event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Expires at"
                      type="datetime-local"
                      InputLabelProps={{ shrink: true }}
                      value={form.expiresAt}
                      onChange={event =>
                        update('expiresAt', event.target.value)
                      }
                    />
                  </Grid>
                  {(
                    [
                      ['maxActiveEnvironments', 'Max environments'],
                      ['maxProviders', 'Max providers'],
                      ['maxApplications', 'Max applications'],
                      ['maxResources', 'Max resources'],
                      ['maxEstimatedMonthlyCost', 'Monthly cost ceiling'],
                      ['maxLeaseDays', 'Max lease days'],
                      ['maxIdleHours', 'Max idle hours'],
                      [
                        'maxEphemeralEnvironments',
                        'Max ephemeral environments',
                      ],
                      ['maxEphemeralTtlHours', 'Max ephemeral TTL hours'],
                      ['maxEphemeralTotalCost', 'Ephemeral lifetime budget'],
                    ] as Array<[keyof EntitlementFormState, string]>
                  ).map(([field, label]) => (
                    <Grid item xs={6} md={3} key={field}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        label={label}
                        type="number"
                        inputProps={{ min: 0 }}
                        value={form[field] as string}
                        onChange={event => update(field, event.target.value)}
                      />
                    </Grid>
                  ))}
                  {(
                    [
                      ['idleAction', 'Idle action'],
                      ['leaseExpiryAction', 'Lease expiry action'],
                    ] as Array<['idleAction' | 'leaseExpiryAction', string]>
                  ).map(([field, label]) => (
                    <Grid item xs={6} md={3} key={field}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel>{label}</InputLabel>
                        <Select
                          label={label}
                          value={form[field]}
                          onChange={event =>
                            update(
                              field,
                              event.target.value as
                                | ''
                                | 'notify'
                                | 'stop'
                                | 'destroy',
                            )
                          }
                        >
                          <MenuItem value="">
                            No automatic recommendation
                          </MenuItem>
                          <MenuItem value="notify">Notify only</MenuItem>
                          <MenuItem value="stop">Recommend stop</MenuItem>
                          <MenuItem value="destroy">Recommend destroy</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      color="primary"
                      variant="contained"
                      startIcon={<AddCircleOutlineIcon />}
                      disabled={submitting || loading}
                      onClick={approve}
                    >
                      {submitting ? 'Approving…' : 'Approve entitlement'}
                    </Button>
                  </Grid>
                </Grid>
              </InfoCard>
            </Grid>

            <Grid item xs={12}>
              {loading && <Progress />}
              <Table
                title={`Project Entitlements (${entitlements.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'ID', field: 'id' },
                  { title: 'Project', field: 'projectRef' },
                  {
                    title: 'Subjects',
                    render: row => row.subjectRefs.join(', '),
                  },
                  { title: 'Capability', render: row => packSummary(row) },
                  { title: 'Cloud scope', render: row => scopeSummary(row) },
                  {
                    title: 'Status',
                    render: row => row.status ?? 'active',
                  },
                  {
                    title: 'Expires',
                    render: row =>
                      row.expiresAt
                        ? new Date(row.expiresAt).toLocaleString()
                        : 'No expiry',
                  },
                ]}
                data={entitlements}
                actions={[
                  {
                    icon: () => <BlockIcon />,
                    tooltip: 'Revoke entitlement',
                    disabled: Boolean(revoking),
                    onClick: (_event, row) => {
                      if (
                        !Array.isArray(row) &&
                        (row as ProjectEntitlement).status !== 'revoked'
                      )
                        revoke(row as ProjectEntitlement);
                    },
                  },
                  {
                    icon: () => <RefreshIcon />,
                    tooltip: 'Refresh governance data',
                    isFreeAction: true,
                    onClick: load,
                  },
                ]}
              />
            </Grid>

            <Grid item xs={12}>
              <InfoCard title="Compose Approved Architecture">
                <Typography paragraph>
                  Create a versioned capability pack from the templates
                  published in this edition. Entitlements grant this exact
                  template set to managers and engineers through their permitted
                  operations.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Pack ID"
                      helperText="For example payments-serverless"
                      value={packForm.id}
                      onChange={event =>
                        updatePackForm('id', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Version"
                      value={packForm.version}
                      onChange={event =>
                        updatePackForm('version', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      label="Title"
                      value={packForm.title}
                      onChange={event =>
                        updatePackForm('title', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Description"
                      value={packForm.description}
                      onChange={event =>
                        updatePackForm('description', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      Architecture cost and tag governance
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      The estimate applies to one deployment of the complete
                      architecture. ARTA system tags are always added and cannot
                      be overridden.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      label="Reviewed architecture monthly estimate"
                      value={packForm.estimatedMonthlyCost}
                      onChange={event =>
                        updatePackForm(
                          'estimatedMonthlyCost',
                          event.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Mandatory organization tags"
                      helperText="Comma or newline separated key=value pairs"
                      value={packForm.mandatoryTags}
                      onChange={event =>
                        updatePackForm('mandatoryTags', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Allowed additional tag keys"
                      helperText="Leave empty to allow any non-ARTA key"
                      value={packForm.allowedAdditionalTagKeys}
                      onChange={event =>
                        updatePackForm(
                          'allowedAdditionalTagKeys',
                          event.target.value,
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={packForm.allowAdditionalTags}
                          onChange={event =>
                            updatePackForm(
                              'allowAdditionalTags',
                              event.target.checked,
                            )
                          }
                        />
                      }
                      label="Allow manager/engineer tags"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="number"
                      inputProps={{ min: 0, max: 50 }}
                      label="Maximum additional tags"
                      value={packForm.maxAdditionalTags}
                      onChange={event =>
                        updatePackForm('maxAdditionalTags', event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">
                      Architecture templates
                    </Typography>
                  </Grid>
                  {templateOptions.map(template => {
                    const selected = packForm.templateRefs.includes(
                      template.entityRef,
                    );
                    return (
                      <Grid item xs={12} key={template.entityRef}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={selected}
                              onChange={event => {
                                const templateRefs = event.target.checked
                                  ? [
                                      ...packForm.templateRefs,
                                      template.entityRef,
                                    ]
                                  : packForm.templateRefs.filter(
                                      ref => ref !== template.entityRef,
                                    );
                                updatePackForm('templateRefs', templateRefs);
                              }}
                            />
                          }
                          label={`${template.title} — ${template.operation}`}
                        />
                      </Grid>
                    );
                  })}
                  <Grid item xs={12}>
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={publishingPack}
                      onClick={createCapabilityPack}
                    >
                      {publishingPack ? 'Creating…' : 'Create draft pack'}
                    </Button>
                  </Grid>
                </Grid>
              </InfoCard>
            </Grid>

            <Grid item xs={12} md={5}>
              <Table
                title={`Capability Packs (${packs.length})`}
                options={{ paging: false, search: true }}
                columns={[
                  { title: 'Title', field: 'title' },
                  { title: 'ID', field: 'id' },
                  { title: 'Version', field: 'version' },
                  { title: 'Owner', field: 'ownerRef' },
                  {
                    title: 'Templates',
                    render: row => String(row.templates.length),
                  },
                  {
                    title: 'Monthly estimate',
                    render: row =>
                      row.estimatedMonthlyCost === undefined
                        ? 'Not reviewed'
                        : `USD ${row.estimatedMonthlyCost.toFixed(2)}`,
                  },
                  {
                    title: 'Additional tags',
                    render: row =>
                      row.taggingPolicy?.allowAdditionalTags === false
                        ? 'Disabled'
                        : `Up to ${row.taggingPolicy?.maxAdditionalTags ?? 20}`,
                  },
                  {
                    title: 'Status',
                    render: row => row.status ?? 'active',
                  },
                  {
                    title: 'Review',
                    render: row => row.reviewStatus ?? 'approved',
                  },
                  {
                    title: 'Lifecycle',
                    render: row => {
                      const review = row.reviewStatus ?? 'approved';
                      const lifecycle = row.status ?? 'active';
                      return (
                        <>
                          {['draft', 'rejected'].includes(review) && (
                            <Button
                              size="small"
                              onClick={() => reviewPack(row, 'in-review')}
                            >
                              Submit review
                            </Button>
                          )}
                          {review === 'in-review' && (
                            <>
                              <Button
                                size="small"
                                color="primary"
                                onClick={() => reviewPack(row, 'approved')}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                onClick={() => reviewPack(row, 'rejected')}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {review === 'approved' &&
                            lifecycle === 'inactive' && (
                              <Button
                                size="small"
                                onClick={() =>
                                  changePackLifecycle(row, 'active')
                                }
                              >
                                Activate
                              </Button>
                            )}
                          {lifecycle === 'active' && (
                            <Button
                              size="small"
                              onClick={() =>
                                changePackLifecycle(row, 'deprecated')
                              }
                            >
                              Deprecate
                            </Button>
                          )}
                          {lifecycle === 'deprecated' && (
                            <>
                              <Button
                                size="small"
                                onClick={() =>
                                  changePackLifecycle(row, 'active')
                                }
                              >
                                Reactivate
                              </Button>
                              <Button
                                size="small"
                                onClick={() =>
                                  changePackLifecycle(row, 'retired')
                                }
                              >
                                Retire
                              </Button>
                            </>
                          )}
                        </>
                      );
                    },
                  },
                ]}
                data={packs}
              />
            </Grid>
            <Grid item xs={12}>
              <InfoCard title="Request Existing Consumer Migration">
                <Typography paragraph>
                  Select an active approved target version. The migration runner
                  must create a reviewed repository proposal and preserve the
                  listed customer-owned paths.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Catalog entity ref"
                      value={upgradeEntityRef}
                      onChange={event =>
                        setUpgradeEntityRef(event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Target pack version</InputLabel>
                      <Select
                        label="Target pack version"
                        value={upgradePackKey}
                        onChange={event => {
                          setUpgradePackKey(String(event.target.value));
                          setUpgradeTemplateRef('');
                        }}
                      >
                        {packs
                          .filter(
                            pack =>
                              (pack.status ?? 'active') === 'active' &&
                              (pack.reviewStatus ?? 'approved') === 'approved',
                          )
                          .map(pack => (
                            <MenuItem
                              key={`${pack.id}@${pack.version}`}
                              value={`${pack.id}@${pack.version}`}
                            >
                              {pack.id}@{pack.version}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Target template</InputLabel>
                      <Select
                        label="Target template"
                        value={upgradeTemplateRef}
                        onChange={event =>
                          setUpgradeTemplateRef(String(event.target.value))
                        }
                      >
                        {(
                          packs.find(
                            pack =>
                              `${pack.id}@${pack.version}` === upgradePackKey,
                          )?.templates ?? []
                        ).map(template => (
                          <MenuItem
                            key={`${template.entityRef}:${template.operation}`}
                            value={template.entityRef}
                          >
                            {template.entityRef} — {template.operation}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Additional preserved paths (comma separated)"
                      value={upgradePreservePaths}
                      onChange={event =>
                        setUpgradePreservePaths(event.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Migration reason"
                      value={upgradeReason}
                      onChange={event => setUpgradeReason(event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={requestTemplateMigration}
                    >
                      Request reviewed migration
                    </Button>
                  </Grid>
                </Grid>
              </InfoCard>
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Template Migration Queue (${templateMigrations.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Entity', field: 'entityRef' },
                  { title: 'Pack', field: 'capabilityPackId' },
                  { title: 'From', field: 'fromVersion' },
                  { title: 'To', field: 'toVersion' },
                  {
                    title: 'Preserve',
                    render: row => row.preservePaths.join(', '),
                  },
                  { title: 'Reason', field: 'reason' },
                  { title: 'Status', field: 'status' },
                  {
                    title: 'Proposal',
                    render: row =>
                      row.proposalUrl ? (
                        <a
                          href={row.proposalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open review
                        </a>
                      ) : (
                        ''
                      ),
                  },
                  {
                    title: 'Decision',
                    render: row =>
                      row.status === 'requested' ? (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => approveTemplateMigration(row)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            onClick={() => cancelTemplateMigration(row)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : row.status === 'approved' ? (
                        <Button
                          size="small"
                          onClick={() => cancelTemplateMigration(row)}
                        >
                          Cancel
                        </Button>
                      ) : (
                        ''
                      ),
                  },
                ]}
                data={templateMigrations}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Cost and Capacity Reservations (${reservations.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Resource', field: 'resourceKey' },
                  { title: 'Project', field: 'projectRef' },
                  { title: 'Operation', field: 'operation' },
                  {
                    title: 'Class',
                    render: row => row.environmentClass ?? 'persistent',
                  },
                  {
                    title: 'Trigger',
                    render: row => row.ephemeral?.trigger ?? '',
                  },
                  {
                    title: 'Data seed',
                    render: row => row.ephemeral?.dataSeedRef ?? '',
                  },
                  {
                    title: 'Status',
                    render: row =>
                      row.cleanupRequired
                        ? `${row.status} · cleanup required`
                        : row.leaseWarningAt
                        ? `${row.status} · lease expiring`
                        : row.status,
                  },
                  {
                    title: 'Monthly estimate',
                    render: row =>
                      row.estimatedMonthlyCost === undefined
                        ? 'Not configured'
                        : String(row.estimatedMonthlyCost),
                  },
                  {
                    title: 'Lifetime estimate',
                    render: row =>
                      row.estimatedLifetimeCost === undefined
                        ? ''
                        : String(row.estimatedLifetimeCost),
                  },
                  {
                    title: 'Lease expires',
                    render: row =>
                      row.leaseExpiresAt
                        ? new Date(row.leaseExpiresAt).toLocaleString()
                        : 'No lease',
                  },
                  { title: 'Requested by', field: 'actorRef' },
                  { title: 'Catalog entity', field: 'catalogEntityRef' },
                  {
                    title: 'Pipeline run',
                    render: row =>
                      row.pipelineUrl ? (
                        <a
                          href={row.pipelineUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {row.pipelineProvider ?? 'pipeline'} #
                          {row.pipelineRunId}
                        </a>
                      ) : row.pipelineRunId ? (
                        `${row.pipelineProvider ?? 'pipeline'} #${
                          row.pipelineRunId
                        }`
                      ) : (
                        'Pending'
                      ),
                  },
                ]}
                data={reservations}
                actions={[
                  {
                    icon: () => <AddAlarmIcon />,
                    tooltip: 'Approve lease extension',
                    onClick: (_event, row) => {
                      if (
                        !Array.isArray(row) &&
                        Boolean(row.leaseExpiresAt) &&
                        ['provisioning', 'active'].includes(row.status)
                      )
                        extendLease(row as QuotaReservation);
                    },
                  },
                  {
                    icon: () => <BlockIcon />,
                    tooltip: 'Verify cleanup and release reservation',
                    onClick: (_event, row) => {
                      if (
                        !Array.isArray(row) &&
                        !['released', 'failed'].includes(row.status)
                      )
                        releaseReservation(row as QuotaReservation);
                    },
                  },
                ]}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Architecture Cost Tracking (${costObservations.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  {
                    title: 'Architecture',
                    render: row =>
                      `${row.capabilityPackId}@${row.capabilityPackVersion}`,
                  },
                  { title: 'Project', field: 'projectRef' },
                  {
                    title: 'Environment',
                    render: row =>
                      row.tags?.['arta-environment'] ?? 'Unclassified',
                  },
                  {
                    title: 'Deployment',
                    render: row => row.deploymentId ?? row.reservationId,
                  },
                  { title: 'Cloud', field: 'source' },
                  {
                    title: 'Actual cost',
                    render: row => `${row.currency} ${row.amount.toFixed(2)}`,
                  },
                  {
                    title: 'Period end',
                    render: row => new Date(row.periodEnd).toLocaleDateString(),
                  },
                  {
                    title: 'Tags',
                    render: row =>
                      Object.entries(row.tags ?? {})
                        .map(([key, value]) => `${key}=${value}`)
                        .join(', '),
                  },
                ]}
                data={costObservations}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Lifecycle Action Queue (${lifecycleActions.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Service', field: 'entityRef' },
                  { title: 'Project', field: 'projectRef' },
                  { title: 'Cloud', field: 'cloud' },
                  { title: 'Action', field: 'action' },
                  { title: 'Trigger', field: 'trigger' },
                  { title: 'Reason', field: 'reason' },
                  { title: 'Status', field: 'status' },
                  { title: 'Approved by', field: 'approvedBy' },
                  { title: 'Execution', field: 'executionReference' },
                  { title: 'Result', field: 'resultMessage' },
                  {
                    title: 'Decision',
                    render: item =>
                      item.status === 'recommended' ? (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => approveLifecycleAction(item)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            onClick={() => cancelLifecycleAction(item)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : item.status === 'approved' ? (
                        <Button
                          size="small"
                          onClick={() => cancelLifecycleAction(item)}
                        >
                          Cancel
                        </Button>
                      ) : (
                        ''
                      ),
                  },
                ]}
                data={lifecycleActions}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Table
                title={`Scorecard Policies (${scorecardDefinitions.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  {
                    title: 'Scorecard',
                    render: item => `${item.id}@${item.version}`,
                  },
                  { title: 'Title', field: 'title' },
                  { title: 'Owner', field: 'ownerRef' },
                  { title: 'Checks', render: item => item.checks.length },
                  {
                    title: 'Active',
                    render: item => (item.active ? 'yes' : 'no'),
                  },
                ]}
                data={scorecardDefinitions}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Table
                title={`Entity Assurance (${scorecards.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Entity', field: 'entityRef' },
                  {
                    title: 'Scorecard',
                    render: item =>
                      `${item.scorecardId}@${item.scorecardVersion}`,
                  },
                  { title: 'Status', field: 'status' },
                  { title: 'Score', render: item => `${item.score}%` },
                  {
                    title: 'Passed',
                    render: item =>
                      `${item.passedChecks}/${item.evaluatedChecks}`,
                  },
                  {
                    title: 'Evaluated',
                    render: item => new Date(item.evaluatedAt).toLocaleString(),
                  },
                ]}
                data={scorecards}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Table
                title={`Drift Observations (${driftObservations.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Entity', field: 'entityRef' },
                  { title: 'Cloud', field: 'provider' },
                  { title: 'Status', field: 'status' },
                  { title: 'Changes', render: item => item.differences.length },
                  {
                    title: 'Critical',
                    render: item =>
                      item.differences.filter(
                        difference => difference.severity === 'critical',
                      ).length,
                  },
                  {
                    title: 'Observed',
                    render: item => new Date(item.observedAt).toLocaleString(),
                  },
                  {
                    title: 'Evidence',
                    render: item =>
                      item.sourceReference ? (
                        <a
                          href={item.sourceReference}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        ''
                      ),
                  },
                ]}
                data={driftObservations}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Table
                title={`Drift Reconciliation Queue (${driftReconciliations.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Entity', field: 'entityRef' },
                  { title: 'Cloud', field: 'provider' },
                  { title: 'Strategy', field: 'strategy' },
                  { title: 'Reason', field: 'reason' },
                  { title: 'Status', field: 'status' },
                  { title: 'Requested by', field: 'requestedBy' },
                  {
                    title: 'Execution',
                    render: item =>
                      item.executionReference ? (
                        <a
                          href={item.executionReference}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        ''
                      ),
                  },
                  {
                    title: 'Decision',
                    render: item =>
                      item.status === 'requested' ? (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => approveDriftReconciliation(item)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            onClick={() => cancelDriftReconciliation(item)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : item.status === 'approved' ? (
                        <Button
                          size="small"
                          onClick={() => cancelDriftReconciliation(item)}
                        >
                          Cancel
                        </Button>
                      ) : (
                        ''
                      ),
                  },
                ]}
                data={driftReconciliations}
              />
            </Grid>
            <Grid item xs={12}>
              <Table
                title={`Deployment Operation Queue (${deploymentOperations.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  { title: 'Service', field: 'entityRef' },
                  { title: 'Project', field: 'projectRef' },
                  { title: 'Provider', field: 'provider' },
                  { title: 'Action', field: 'action' },
                  { title: 'Target revision', field: 'targetRevision' },
                  { title: 'Reason', field: 'reason' },
                  { title: 'Status', field: 'status' },
                  { title: 'Requested by', field: 'requestedBy' },
                  { title: 'Execution', field: 'executionReference' },
                  { title: 'Result', field: 'resultMessage' },
                  {
                    title: 'Decision',
                    render: item =>
                      item.status === 'requested' ? (
                        <>
                          <Button
                            size="small"
                            color="primary"
                            onClick={() => approveDeploymentOperation(item)}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            onClick={() => cancelDeploymentOperation(item)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : item.status === 'approved' ? (
                        <Button
                          size="small"
                          onClick={() => cancelDeploymentOperation(item)}
                        >
                          Cancel
                        </Button>
                      ) : (
                        ''
                      ),
                  },
                ]}
                data={deploymentOperations}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Table
                title={`Authorization Audit (${audit.length})`}
                options={{ paging: true, pageSize: 10, search: true }}
                columns={[
                  {
                    title: 'Time',
                    render: row => new Date(row.createdAt).toLocaleString(),
                  },
                  { title: 'Action', field: 'action' },
                  { title: 'Outcome', field: 'outcome' },
                  { title: 'Actor', field: 'actor' },
                  { title: 'Entitlement', field: 'entitlementId' },
                ]}
                data={audit}
              />
            </Grid>
          </Grid>
        )}
      </Content>
    </Page>
  );
};
